import React, {useState} from 'react';
import ReactApexCharts from 'react-apexcharts';
import Card from 'components/PurpleTheme/Card';
import { Dropdown, DropdownMenu, DropdownItem, DropdownToggle } from 'reactstrap';

const LineChart = ({ data, setTimeframe, disableFilters, height, timeFrame, hideLabels, chartType }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hours, setHours] = useState(timeFrame);
  const fetchChartOptions = () => {
    return {
      stroke :{
        width: chartType === 'area' ? 0.9 : 2,
      },
      chart: {
        type: 'line',
        height: height,
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false
      },
      zoom: {
        enabled: true,
      },
      markers: {
        size: 0,  
      },
      xaxis: {
        type: 'datetime',
        labels: hideLabels ? { show: false } : {
          format: hours > 168 ? 'dd MMM' : (hours > 48 ? "ddd hh:mm" : "hh:mm"),
          datetimeUTC: false
        }
      },
      yaxis: {
        title: hideLabels ? { show: false } : { text: 'Response Time (ms)' },
        labels: {
          show: !hideLabels,
          formatter: function (value) {
            return value.toFixed(0) + " ms";
          },
        },
      },
      grid: hideLabels ? { show: false } : {
        borderColor: '#30243F',
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 15
        }
      },
      tooltip: hideLabels ? { enabled: false } : {
        x: {
          show: true,
          format: 'd MMM, hh:mm TT',
          formatter: undefined,
          hideEmptySeries: false,
        },
      },
    };
  };
  

  const [chartOptions, setChartOptions] = useState(fetchChartOptions());
  let totalUptime = 0;
  let count = 0;
  const current_date = new Date();
  const current_time = parseInt(current_date.getTime() / 60000 - 1) * 60000 //

  let increment_in_minutes, currentTimeOffset;
  if (hours > 7 * 24){
    let midnight = new Date(current_date.getFullYear(), current_date.getMonth(), current_date.getDate()).getTime();
    currentTimeOffset = current_time - midnight
    increment_in_minutes = 24 * 60 * 60 * 1000;
  } else if(hours > 24){
    let lastHour = new Date(current_date.getFullYear(), current_date.getMonth(), current_date.getDate(), current_date.getHours()).getTime();
    currentTimeOffset = current_time - lastHour;
    increment_in_minutes = 60 * 60 * 1000;
  } else{
    currentTimeOffset = 0;
    increment_in_minutes = 60 * 1000;
  }
  const start_time = current_time - hours * 60 * 60 * 1000 - currentTimeOffset - current_date.getTimezoneOffset() * 60000;
  const uptimeDict = {};
  const responseTimeDict = {};
  for(let i = start_time; i <= current_time; i += increment_in_minutes){

    uptimeDict[i] = [i, 0];
    responseTimeDict[i] = [i,0];
  }

  for (const key of Object.keys(data)) {
    if (key === "organization_id" || key === "application_id") {
      continue;
    }
    const date = new Date(key);
    const timestamp = getTimestampFromDate(date) + new Date().getTimezoneOffset() * 60000;
    const isUp = data[key].is_up;
    const resTime = data[key].response_time || 0;

    uptimeDict[timestamp] = [timestamp, isUp];
    responseTimeDict[timestamp] = [timestamp, isUp === 1 ? resTime: 0 ];

    totalUptime += isUp;
    count++;
  }

  const uptime = Object.values(uptimeDict).sort((a,b) => a[0] - b[0])
  const responseTime = Object.values(responseTimeDict).sort((a,b) => a[0] - b[0])


  function getTimestampFromDate(date){
    return date.getTime() - date.getTimezoneOffset()*60*1000;
  }

  function calculatePercentile(original, percentile) {
    if (!original || !original.length){
      return 0
    }
    const arr = [...original]
    arr.sort((a, b) => a[1] - b[1]);
    const index = Math.ceil(percentile / 100 * arr.length) - 1;
    return arr[index][1];
  }
  const getUptimePercentage = (totalUptime, count) => {
    const percentage = (count > 0 ? totalUptime / count : 0) * 100;
    const [whole, decimal] = percentage.toString().split('.');
    return whole + '.' + (decimal?.slice(0, 2) || '00');
  };
  const avgUptime = getUptimePercentage(totalUptime, count);
  const averageResponseTime = (calculatePercentile(responseTime, "95") || 0).toFixed(0)

  const updateTimeframe = (hours) => {
    setTimeframe(hours);
    setHours(hours);
    setChartOptions(fetchChartOptions())
  }

  const series = [{
    name: 'Response Time',
    data: responseTime
  }];

  return (
    <div>
       {disableFilters === false ? <hr style={{borderColor:"#69656e"}}></hr> : null}
       {disableFilters === false ?
       <div className="w-100 d-flex justify-content-between">
       <div className='d-flex justify-content-center align-items-center gap-3 ms-4'>
         <h5>Response time by endpoint</h5>
         <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)} direction={"down"}>
         <DropdownToggle color="primary" style={{ border:"none"}} caret>
           {hours === 1 && "Last hour"}
           {hours > 1 && hours < 24 && `Last ${hours} hours`}
           {hours >= 24 && hours < 48 && "Last day"}
           {hours >= 48 && hours < 168 && `Last ${Math.floor(hours / 24)} days`}
           {hours === 168 && "Last week"}
           {hours > 168 && "Last month"}
           <div className="bx bx-caret-down ps-1"></div>
         </DropdownToggle>
         <DropdownMenu>
           <DropdownItem onClick={() => updateTimeframe(1)}>Last hour</DropdownItem>
           <DropdownItem onClick={() => updateTimeframe(3)}>Last 3 hours</DropdownItem>
           <DropdownItem onClick={() => updateTimeframe(6)}>Last 6 hours</DropdownItem>
           <DropdownItem onClick={() => updateTimeframe(24)}>Last 24 hours</DropdownItem>
           <DropdownItem onClick={() => updateTimeframe(72)}>Last 3 days</DropdownItem>
           <DropdownItem onClick={() => updateTimeframe(168)}>Last week</DropdownItem>
           <DropdownItem onClick={() => updateTimeframe(720)}>Last Month</DropdownItem>
         </DropdownMenu>
       </Dropdown>
       </div>
       <div className='d-flex gap-3'>
         <Card className="card-transparent d-flex justify-content-center align-items-center" minHeight="70px" bg="#423b3e34" width="160px">
           <div className="d-flex flex-column justify-content-center gap-2 align-items-center">
             <span className="text-white">Uptime</span>
             <span className="h4">{avgUptime}%</span>
           </div>
         </Card>
         <Card className="card-transparent d-flex justify-content-center align-items-center" minHeight="70px" bg="#423b3e34" width="180px">
           <div className="d-flex flex-column justify-content-center gap-2 align-items-center">
             <span className="text-white">Avg Response Time</span>
             <span className="h4">{averageResponseTime} ms</span>
           </div>
         </Card>
       </div> 
     </div> : null
      }
        
      <div>
        <ReactApexCharts options={chartOptions} series={series} type={chartType} height={height} />
      </div>
    </div>
    
  );
};

export default LineChart;
